import * as v5 from '@/api/v5'
import type { ElectricityAggregateType } from '@/types/api'

/** One hour of merged history: energy in Wh, time is the UTC hour start (ISO). */
export interface HourlySlot {
  time: string
  delivered: number
  returned: number
  produced: number
}

export interface AddressEnergyHistory {
  slots: HourlySlot[]
  hasSmartMeter: boolean
  hasSolar: boolean
}

/**
 * Never drop an hour below this, so a genuine peak (an EV charging in a house
 * that otherwise uses almost nothing) is never mistaken for an artefact. No
 * domestic connection can deliver more than this in an hour.
 */
const PLAUSIBLE_HOUR_FLOOR_WH = 25_000
/** Above this multiple of the address's own 99th percentile hour, a reading is junk. */
const PLAUSIBLE_HOUR_P99_FACTOR = 20

/** Ceiling for one hour, derived from the address's own consumption, so that a
 * large commercial connection is not held to a domestic limit. */
function plausibleHourCeilingWh(values: number[]): number {
  const positive = values.filter((wh) => wh > 0).sort((a, b) => a - b)
  if (positive.length === 0) return PLAUSIBLE_HOUR_FLOOR_WH
  const p99 = positive[Math.min(positive.length - 1, Math.floor(positive.length * 0.99))]
  return Math.max(PLAUSIBLE_HOUR_FLOOR_WH, p99 * PLAUSIBLE_HOUR_P99_FACTOR)
}

/**
 * Meter series come from cumulative counters, so a reset or a correction surfaces
 * as a pair of absurd hours: production data holds addresses with one hour of
 * −249 MWh and another of +249 MWh, in a home whose median hour is 0,18 kWh.
 * They cancel in a yearly total, which is exactly why they are easy to miss, and
 * either one alone is enough to wreck the economics. Drop both: energy is never
 * negative, and no hour can be orders of magnitude above what this address
 * otherwise uses.
 */
export function sanitizeSlots(slots: HourlySlot[]): HourlySlot[] {
  const ceiling = {
    delivered: plausibleHourCeilingWh(slots.map((s) => s.delivered)),
    returned: plausibleHourCeilingWh(slots.map((s) => s.returned)),
    produced: plausibleHourCeilingWh(slots.map((s) => s.produced)),
  }
  const metered = (wh: number, max: number) =>
    Number.isFinite(wh) && wh > 0 && wh <= max ? wh : 0
  return slots.map((slot) => ({
    time: slot.time,
    delivered: metered(slot.delivered, ceiling.delivered),
    returned: metered(slot.returned, ceiling.returned),
    produced: metered(slot.produced, ceiling.produced),
  }))
}

/** Round a timestamp down to its UTC hour so all series share slot keys. */
export function hourKey(iso: string): string {
  const d = new Date(iso)
  d.setUTCMinutes(0, 0, 0)
  return d.toISOString()
}

interface FetchOptions {
  signal: AbortSignal
  /** Called as requests complete (unit = one HTTP request). */
  onRequestDone?: () => void
}

/**
 * Devices whose type indicates a P1 smart meter. The API uses several spellings
 * for the same thing: `smartMeter` and `sparky` are the common ones, `meter` is
 * a DSMR meter read directly, and `flint` is Chargee's own reader. Anything that
 * delivers P1 data belongs here; a meter filed under an unlisted type used to
 * read as "no smart meter" while its data was already on screen.
 */
export function isSmartMeterType(type: string): boolean {
  const norm = type?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') ?? ''
  return [
    'smart_meter',
    'smartmeter',
    'sparky',
    'flint',
    'meter',
    'dsmr',
    'p1',
    'p1_meter',
    'p1meter',
  ].includes(norm)
}

/** Devices whose type indicates a solar inverter. */
function isSolarInverterType(type: string): boolean {
  const norm = type?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') ?? ''
  return norm === 'solar_inverter' || norm === 'inverter'
}

/** Devices whose type indicates a home battery. */
function isBatteryType(type: string): boolean {
  const norm = type?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') ?? ''
  return norm === 'battery' || norm === 'home_battery' || norm === 'homebattery'
}

/**
 * Measured grid energy means P1 data reached us, whatever the device list says.
 * The advice needs the data, not the device record, so this is the signal that
 * decides whether we can report.
 */
export function hasMeteredEnergy(slots: HourlySlot[]): boolean {
  return slots.some((slot) => slot.delivered > 0 || slot.returned > 0)
}

export async function getAddressCapabilities(
  addressUuid: string,
  signal: AbortSignal
): Promise<{ hasSmartMeter: boolean; hasSolar: boolean }> {
  const devices = await v5.getAddressDevices(addressUuid, signal)
  const results = devices.results ?? []
  return {
    hasSmartMeter: results.some((d) => isSmartMeterType(d.type)),
    hasSolar: results.some((d) => isSolarInverterType(d.type)),
  }
}

export interface AddressSituation {
  /** Start of the first day with measured grid data, or null when none. */
  earliest: Date | null
  /** Whole months of history available from `earliest` to today, capped at 12. */
  months: number
  hasSmartMeter: boolean
  hasSolar: boolean
  hasBattery: boolean
  /** kWh totals over the probed window (up to 1 year). */
  deliveredKwh: number
  returnedKwh: number
  producedKwh: number | null
  consumedKwh: number | null
  /**
   * Share of own solar production the household uses directly today, without
   * a battery: (produced − returned) / produced. Null when production data is
   * missing (e.g. no inverter connected).
   */
  selfConsumptionPercent: number | null
}

async function fetchDailySeries(
  addressUuid: string,
  aggregateType: ElectricityAggregateType,
  fromDate: string,
  toDate: string,
  timezone: string,
  signal: AbortSignal
) {
  const res = await v5.getAddressEnergyElectricityAggregation(
    addressUuid,
    { aggregateType, resolution: 'daily', fromDate, toDate, timezone },
    signal
  )
  return res.results ?? []
}

/**
 * Probe the situation of an address before running the report: how much
 * measurement history exists (to suggest the simulation timeframe, max 1
 * year), which devices are connected, and — when the meter shows grid return
 * — the current self-consumption rate from the produced/consumed series.
 */
export async function probeAddressSituation(
  addressUuid: string,
  signal: AbortSignal
): Promise<AddressSituation> {
  const to = new Date()
  to.setHours(0, 0, 0, 0)
  const from = new Date(to)
  from.setMonth(from.getMonth() - 12)
  const fromDate = from.toISOString()
  const toDate = to.toISOString()
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const [capabilitiesRes, delivered, returned] = await Promise.all([
    v5.getAddressDevices(addressUuid, signal),
    fetchDailySeries(addressUuid, 'delivered', fromDate, toDate, timezone, signal),
    fetchDailySeries(addressUuid, 'returned', fromDate, toDate, timezone, signal),
  ])

  const devices = capabilitiesRes.results ?? []
  const hasSolar = devices.some((d) => isSolarInverterType(d.type))
  const hasBattery = devices.some((d) => isBatteryType(d.type))

  const sumKwh = (rows: Array<{ value: number }>) =>
    rows.reduce((sum, r) => sum + Math.max(0, r.value), 0) / 1000
  const deliveredKwh = sumKwh(delivered)
  const returnedKwh = sumKwh(returned)
  // Metered energy is proof of a P1 feed even when the device is typed oddly.
  const hasSmartMeter =
    devices.some((d) => isSmartMeterType(d.type)) || deliveredKwh > 0 || returnedKwh > 0

  // First day with any grid activity marks the start of usable history.
  const firstWithData = [...delivered, ...returned]
    .filter((r) => r.value > 0)
    .sort((a, b) => a.time.localeCompare(b.time))[0]
  const earliest = firstWithData ? new Date(firstWithData.time) : null
  const monthSpan = earliest
    ? (to.getFullYear() - earliest.getFullYear()) * 12 + (to.getMonth() - earliest.getMonth())
    : 0
  const months = earliest ? Math.min(12, Math.max(1, monthSpan)) : 0

  // Only when the meter shows return is there solar production worth reading.
  let producedKwh: number | null = null
  let consumedKwh: number | null = null
  if (returnedKwh > 0) {
    const [produced, consumed] = await Promise.all([
      fetchDailySeries(addressUuid, 'produced', fromDate, toDate, timezone, signal).catch(
        () => null
      ),
      // `consumed` is not supported on every Companion environment.
      fetchDailySeries(addressUuid, 'consumed', fromDate, toDate, timezone, signal).catch(
        () => null
      ),
    ])
    producedKwh = produced ? sumKwh(produced) : null
    consumedKwh = consumed ? sumKwh(consumed) : null
  }

  const selfConsumptionPercent =
    producedKwh != null && producedKwh > 0
      ? Math.round(
          Math.min(100, Math.max(0, ((producedKwh - returnedKwh) / producedKwh) * 100))
        )
      : null

  return {
    earliest,
    months,
    hasSmartMeter,
    hasSolar,
    hasBattery,
    deliveredKwh,
    returnedKwh,
    producedKwh,
    consumedKwh,
    selfConsumptionPercent,
  }
}

/** Number of HTTP requests fetchAddressHourlyHistory will make (one per series). */
export function hourlyHistoryRequestCount(): number {
  return 3
}

/**
 * Fetch one address's energy history (grid delivery/return and solar
 * production via the v5 electricity aggregation endpoint) and merge it into
 * time-ordered slots. The whole period is fetched in a single request per
 * series, like companion-web's battery report.
 */
export async function fetchAddressHistory(
  addressUuid: string,
  from: Date,
  to: Date,
  resolution: 'hourly' | 'daily',
  { signal, onRequestDone }: FetchOptions
): Promise<HourlySlot[]> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const slotMap = new Map<string, HourlySlot>()
  const getSlot = (time: string): HourlySlot => {
    let slot = slotMap.get(time)
    if (!slot) {
      slot = { time, delivered: 0, returned: 0, produced: 0 }
      slotMap.set(time, slot)
    }
    return slot
  }

  const series: Array<{ type: ElectricityAggregateType; apply: (slot: HourlySlot, wh: number) => void }> = [
    { type: 'delivered', apply: (slot, wh) => (slot.delivered += wh) },
    { type: 'returned', apply: (slot, wh) => (slot.returned += wh) },
    { type: 'produced', apply: (slot, wh) => (slot.produced += wh) },
  ]

  await Promise.all(
    series.map(async (s) => {
      const res = await v5.getAddressEnergyElectricityAggregation(
        addressUuid,
        {
          aggregateType: s.type,
          resolution,
          fromDate: from.toISOString(),
          toDate: to.toISOString(),
          timezone,
        },
        signal
      )
      for (const r of res.results ?? []) {
        s.apply(getSlot(hourKey(r.time)), r.value)
      }
      onRequestDone?.()
    })
  )

  return sanitizeSlots([...slotMap.values()].sort((a, b) => a.time.localeCompare(b.time)))
}

/** Hourly energy history of an address (see fetchAddressHistory). */
export async function fetchAddressHourlyHistory(
  addressUuid: string,
  from: Date,
  to: Date,
  options: FetchOptions
): Promise<HourlySlot[]> {
  return fetchAddressHistory(addressUuid, from, to, 'hourly', options)
}

/**
 * Absolute net grid power per hour derived from the hourly slots. An hourly
 * Wh interval equals its average power in W, so no extra API requests are
 * needed — these samples feed the percentile-based inverter sizing.
 */
export function hourlyNetPowerW(slots: HourlySlot[]): number[] {
  return slots.map((slot) => Math.abs(slot.delivered - slot.returned))
}
